import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../context/AuthContext";
import { toast } from "sonner";

// Widget flottant « Un problème ? » du tunnel d'inscription association
// (chantier onboarding 05/08/2026, Lot B — demande Lucas : une asso bloquée
// doit pouvoir signaler le problème en 30 secondes, avec une capture d'écran,
// direct dans la boîte mail admin — depuis TOUTES les pages du tunnel jusqu'à
// la soumission de la demande).
//
// Monté globalement dans App.js ; ne s'affiche QUE pendant la phase
// d'INSCRIPTION (recadrage Lucas 08/08/2026 : « uniquement quand une
// association s'inscrit, jusqu'à ce qu'elle envoie la candidature à
// l'administrateur ») :
//   - pages du formulaire d'inscription (pré-compte) → toujours visible ;
//   - dashboard → visible SEULEMENT tant que la candidature n'est pas envoyée
//     (on attend la réponse /auth/me avant d'afficher : pas de flash pour une
//     asso déjà candidate) ; une asso refusée qui doit corriger le revoit ;
//   - page de CONNEXION, vitrine « devenir partenaire », reste du site → jamais.

const SIGNUP_FORM_PREFIXES = [
  "/association/register",
  "/association/setup-password",
];
const DASHBOARD_PREFIX = "/association/dashboard";

const MAX_MESSAGE = 500;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024; // marge sous la limite backend (10 Mo)

export default function AssoFeedbackWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [screenshot, setScreenshot] = useState(null); // data-URI
  const [sending, setSending] = useState(false);
  const [assoInfo, setAssoInfo] = useState(null); // { association_id, status, is_verified }
  // null = réponse /auth/me pas encore arrivée (on n'affiche rien sur le
  // dashboard tant qu'on ne SAIT pas — jamais de flash pour une asso candidate).
  const [assoInfoResolved, setAssoInfoResolved] = useState(false);
  const fileRef = useRef(null);

  const onSignupForm = SIGNUP_FORM_PREFIXES.some((p) => location.pathname.startsWith(p));
  const onDashboard = location.pathname.startsWith(DASHBOARD_PREFIX);

  // Identité asso (dashboard uniquement — les pages d'inscription n'ont pas de
  // session) : statut de candidature + pré-remplissage de l'email.
  useEffect(() => {
    if (!onDashboard) return;
    let mounted = true;
    setAssoInfoResolved(false);
    api.get("/associations/auth/me")
      .then((r) => {
        if (!mounted) return;
        const a = r.data?.association || r.data || {};
        setAssoInfo({
          association_id: a.association_id || null,
          // Statut de CANDIDATURE (incomplete | pending_admin | active) — le
          // champ ``status`` du compte est un statut de compte, pas du workflow.
          status: a.verification_status || a.status || null,
          is_verified: !!a.is_verified,
          email: a.email || null,
        });
      })
      .catch(() => { if (mounted) setAssoInfo(null); })
      .finally(() => { if (mounted) setAssoInfoResolved(true); });
    return () => { mounted = false; };
  }, [onDashboard, location.pathname]);

  // Disparition IMMÉDIATE à l'envoi de la candidature (même URL — la
  // checklist émet l'événement au succès du POST submit-verification).
  useEffect(() => {
    const onSubmitted = () =>
      setAssoInfo((prev) => ({ ...(prev || {}), status: "pending_admin" }));
    window.addEventListener("asso-verification-submitted", onSubmitted);
    return () => window.removeEventListener("asso-verification-submitted", onSubmitted);
  }, []);

  if (!onSignupForm && !onDashboard) return null;
  if (onDashboard) {
    // Tant qu'on ne connaît pas le statut : rien (pas de flash trompeur).
    if (!assoInfoResolved || !assoInfo) return null;
    // Candidature ENVOYÉE ou asso validée → le widget se retire : il
    // accompagne l'inscription, pas la vie du compte. (Une asso refusée
    // repasse en phase de correction → il revient.)
    if (assoInfo.is_verified || assoInfo.status === "pending_admin") return null;
  }

  const pickImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choisissez une image (capture d'écran, photo).");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image trop lourde (8 Mo maximum).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(String(reader.result));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!message.trim()) {
      toast.error("Décrivez le problème en quelques mots.");
      return;
    }
    setSending(true);
    try {
      await api.post("/associations/feedback", {
        message: message.trim().slice(0, MAX_MESSAGE),
        email: (email || assoInfo?.email || "").trim(),
        page: location.pathname,
        association_id: assoInfo?.association_id || "",
        screenshot,
      });
      toast.success("Merci ! Votre signalement est parti — nous revenons vers vous vite.");
      setOpen(false);
      setMessage("");
      setScreenshot(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Envoi impossible. Réessayez ou écrivez à contact@gively.fr.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed right-4 z-[60]"
      // RÈGLE SAFE AREA + barre de nav basse (conflit signalé par Lucas 17/08 :
      // le bouton flottant recouvrait « Compte » de la barre inférieure). La
      // pastille se place AU-DESSUS de la nav — `--bottom-nav-height` inclut
      // déjà `env(safe-area-inset-bottom)`, on n'y ajoute qu'une respiration.
      style={{ bottom: "calc(var(--bottom-nav-height) + 0.75rem)" }}
    >
      {open && (
        <div
          className="mb-3 w-[min(92vw,22rem)] bg-white border border-slate-200 rounded-2xl shadow-xl p-4 overflow-y-auto"
          style={{ maxHeight: "min(70vh, 30rem)" }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="font-semibold text-slate-800">Un problème pendant votre inscription ?</p>
              <p className="text-xs text-slate-500">
                Décrivez ce qui bloque — l'équipe Gively le reçoit immédiatement.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
            rows={4}
            placeholder={'Exemple : "Je n\'arrive pas à accéder au bouton, Stripe ne fonctionne pas."'}
            className="w-full border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none"
            data-testid="asso-feedback-message"
          />
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span>{message.length}/{MAX_MESSAGE}</span>
          </div>
          {!assoInfo?.email && (
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre email (pour vous répondre)"
              className="w-full border border-slate-200 rounded-xl p-2.5 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-sky-300"
              data-testid="asso-feedback-email"
            />
          )}
          <div className="flex items-center gap-2 mb-3">
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100">
              📎 {screenshot ? "Changer la capture" : "Joindre une capture d'écran"}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={pickImage}
                className="hidden"
                data-testid="asso-feedback-screenshot"
              />
            </label>
            {screenshot && (
              <button
                type="button"
                onClick={() => { setScreenshot(null); if (fileRef.current) fileRef.current.value = ""; }}
                className="text-xs text-rose-500 hover:text-rose-600"
              >
                Retirer
              </button>
            )}
          </div>
          {screenshot && (
            <img
              src={screenshot}
              alt="Aperçu de votre capture d'écran"
              className="w-full max-h-32 object-contain rounded-lg border border-slate-100 mb-3"
            />
          )}
          <button
            type="button"
            onClick={submit}
            disabled={sending || !message.trim()}
            className="w-full py-2.5 rounded-xl bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 disabled:opacity-50 transition-colors"
            data-testid="asso-feedback-submit"
          >
            {sending ? "Envoi…" : "Envoyer à l'équipe Gively"}
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ml-auto flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-lg hover:bg-slate-700 transition-colors"
        data-testid="asso-feedback-fab"
        aria-expanded={open}
      >
        💬 Un problème ?
      </button>
    </div>
  );
}
